import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiError } from '@/shared/api'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { toBrowserMediaUrl } from '@/shared/lib/media-url'
import { SegmentedTabs, type SegmentedTabItem } from '@/shared/ui/segmented-tabs'
import { Button, Input } from '@/shared/ui'

import {
  loadAdminVideoLibraryReferences,
  type ReferenceKind,
  type ReferenceRecord,
} from '../api/video-library-admin-api'

type BannerTone = 'error' | 'success'

type ReferenceBrowserItemState = {
  itemId: string
  linkId?: string | null
}

export type ReferenceBrowserProps = {
  kind: ReferenceKind
  actionLabel: string
  linkedItems?: ReferenceBrowserItemState[]
  onAdd: (referenceId: string) => Promise<void>
  onRemove?: (itemId: string, linkId?: string | null) => Promise<void>
  emptyState?: string
  searchPlaceholder?: string
}

export type ReferenceBrowserTab<T extends string> = {
  id: T
  label: string
  browser: ReferenceBrowserProps
}

type TabbedReferenceBrowserProps<T extends string> = {
  activeId: T
  ariaLabel: string
  tabs: ReferenceBrowserTab<T>[]
  onChange: (next: T) => void
}

const referencePageSize = 10

const smallBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const mutedStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.secondary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const panelStyle = {
  borderColor: designTokens.colors.border.adminPattern,
  backgroundColor: designTokens.colors.background.surface,
}

const formatError = (error: unknown) => {
  if (error instanceof ApiError) {
    return `${error.status} ${error.statusText}`
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Unexpected admin error'
}

const successToneFromMessage = (message: string): BannerTone =>
  message.includes('связ') || message.includes('добав') || message.includes('удал')
    ? 'success'
    : 'error'

function PaginationControls({
  page,
  hasMore,
  total,
  limit,
  onPrevious,
  onNext,
  onSelectPage,
}: {
  page: number
  hasMore: boolean
  total: number
  limit: number
  onPrevious: () => void
  onNext: () => void
  onSelectPage: (page: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const pageNumbers = new Set<number>([1, totalPages, page - 1, page, page + 1, page + 2])
  const visiblePages = Array.from(pageNumbers)
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((left, right) => left - right)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span style={mutedStyle}>Всего: {total}</span>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondaryAssist" disabled={page <= 1} onClick={onPrevious}>
          Назад
        </Button>
        {visiblePages.map((item, index) => {
          const previousPage = visiblePages[index - 1]
          const showEllipsis = previousPage !== undefined && item - previousPage > 1

          return (
            <div key={item} className="flex items-center gap-2">
              {showEllipsis ? <span style={mutedStyle}>…</span> : null}
              <Button
                type="button"
                variant={item === page ? 'primary' : 'secondaryAssist'}
                onClick={() => onSelectPage(item)}
              >
                {item}
              </Button>
            </div>
          )
        })}
        <Button type="button" variant="secondaryAssist" disabled={!hasMore} onClick={onNext}>
          Вперёд
        </Button>
      </div>
    </div>
  )
}

function FeedbackBanner({ message, tone = 'error' }: { message: string; tone?: BannerTone }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        ...smallBodyStyle,
        backgroundColor: tone === 'success' ? '#dcfce7' : '#fee2e2',
        color: tone === 'success' ? '#166534' : '#991b1b',
      }}
    >
      {message}
    </div>
  )
}

function ReferenceBrowserCard({
  item,
  linkedEntry,
  actionLabel,
  busy,
  onAdd,
  onRemove,
}: {
  item: ReferenceRecord
  linkedEntry: ReferenceBrowserItemState | undefined
  actionLabel: string
  busy: boolean
  onAdd: () => void
  onRemove?: () => void
}) {
  const isLinked = Boolean(linkedEntry)
  const coverUrl = toBrowserMediaUrl(item.coverUrl)

  return (
    <div
      className="flex flex-col gap-3 rounded-2xl border p-4 lg:flex-row lg:items-center lg:justify-between"
      style={panelStyle}
    >
      <div className="flex min-w-0 items-start gap-4">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={item.title}
            className="h-16 w-24 shrink-0 rounded-2xl object-cover"
          />
        ) : null}
        <div className="min-w-0 flex flex-col gap-1">
          <strong style={smallBodyStyle}>{item.title}</strong>
          <span style={mutedStyle}>
            {item.urlSlug ?? 'Без slug'}
            {isLinked ? ' · уже связано' : ''}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {isLinked && onRemove ? (
          <Button type="button" variant="secondaryAssist" disabled={busy} onClick={onRemove}>
            {busy ? 'Сохраняю...' : 'Удалить связь'}
          </Button>
        ) : (
          <Button type="button" disabled={busy} onClick={onAdd}>
            {busy ? 'Сохраняю...' : actionLabel}
          </Button>
        )}
      </div>
    </div>
  )
}

export function ReferenceBrowser({
  kind,
  actionLabel,
  linkedItems = [],
  onAdd,
  onRemove,
  emptyState = 'По этому запросу ничего не найдено.',
  searchPlaceholder = 'Поиск по title / slug',
}: ReferenceBrowserProps) {
  const [searchDraft, setSearchDraft] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const linkedByItemId = new Map(linkedItems.map((item) => [item.itemId, item]))

  const referencesQuery = useQuery({
    queryKey: ['admin-video-library', 'references', kind, page, appliedSearch],
    queryFn: () => loadAdminVideoLibraryReferences(kind, page, referencePageSize, appliedSearch),
  })

  const runAdd = async (referenceId: string) => {
    setBusyId(referenceId)
    setFeedback(null)
    try {
      await onAdd(referenceId)
      setFeedback('Связь сохранена')
    } catch (error) {
      setFeedback(formatError(error))
    } finally {
      setBusyId(null)
    }
  }

  const runRemove = async (referenceId: string, linkId?: string | null) => {
    if (!onRemove) {
      return
    }

    setBusyId(referenceId)
    setFeedback(null)
    try {
      await onRemove(referenceId, linkId)
      setFeedback('Связь удалена')
    } catch (error) {
      setFeedback(formatError(error))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border p-4" style={panelStyle}>
      <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto]">
        <Input
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          placeholder={searchPlaceholder}
        />
        <Button
          type="button"
          variant="secondaryAssist"
          onClick={() => {
            setPage(1)
            setAppliedSearch(searchDraft.trim())
          }}
        >
          Применить
        </Button>
        <Button
          type="button"
          variant="secondaryAssist"
          onClick={() => {
            setPage(1)
            setSearchDraft('')
            setAppliedSearch('')
          }}
        >
          Сбросить
        </Button>
      </div>

      {referencesQuery.data ? (
        <PaginationControls
          page={page}
          hasMore={referencesQuery.data.meta.hasMore}
          total={referencesQuery.data.meta.total}
          limit={referencePageSize}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          onSelectPage={setPage}
        />
      ) : null}

      <div className="flex flex-col gap-3">
        {referencesQuery.isLoading ? <p style={mutedStyle}>Загружаю варианты…</p> : null}
        {!referencesQuery.isLoading && (referencesQuery.data?.data.length ?? 0) === 0 ? (
          <p style={mutedStyle}>{emptyState}</p>
        ) : null}
        {(referencesQuery.data?.data ?? []).map((item) => {
          const linkedEntry = linkedByItemId.get(item.id)
          return (
            <ReferenceBrowserCard
              key={item.id}
              item={item}
              linkedEntry={linkedEntry}
              actionLabel={actionLabel}
              busy={busyId === item.id}
              onAdd={() => void runAdd(item.id)}
              onRemove={linkedEntry ? () => void runRemove(item.id, linkedEntry.linkId) : undefined}
            />
          )
        })}
      </div>

      {feedback ? (
        <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
      ) : null}
    </div>
  )
}

export function TabbedReferenceBrowser<T extends string>({
  activeId,
  ariaLabel,
  tabs,
  onChange,
}: TabbedReferenceBrowserProps<T>) {
  const tabItems: SegmentedTabItem<T>[] = tabs.map((tab) => ({
    id: tab.id,
    label: tab.label,
  }))

  const activeTab = tabs.find((tab) => tab.id === activeId) ?? tabs[0]
  if (!activeTab) {
    return null
  }

  return (
    <div className="flex flex-col gap-3">
      <SegmentedTabs
        activeId={activeTab.id}
        ariaLabel={ariaLabel}
        items={tabItems}
        onChange={onChange}
      />
      <ReferenceBrowser {...activeTab.browser} />
    </div>
  )
}
